import { ApiRequestError } from './api-client';
import { t } from './i18n';

/**
 * What to show the person when a request fails.
 *
 * The API answers in English — code, identifiers and messages are English by convention — while the
 * interface is pt-BR (context.md sections 130, 206). Showing the server's sentence verbatim leaks
 * that boundary onto the screen, so known codes are translated here and anything unrecognised falls
 * back to a generic line rather than to English.
 *
 * Codes rather than messages: a message is prose that will be reworded; a code is a contract.
 */
const MESSAGES: Record<string, string> = {
  'network.unreachable': 'error.network',
  'internal.error': 'error.generic',
  'auth.invalid_credentials': 'login.invalidCredentials',
  'auth.token_expired': 'error.sessionExpired',
  'auth.account_disabled': 'error.accountDisabled',
  'authz.denied': 'error.forbidden',
  'billing.no_subscription': 'error.noSubscription',
  'billing.feature_not_entitled': 'error.featureUnavailable',
  'billing.limit_reached': 'error.limitReached',
  'billing.account_read_only': 'error.readOnly',
  'request.validation_failed': 'error.validation',
  'route.not_found': 'error.notFound',
  'resource.not_found': 'error.notFound',
  // The code the API actually sends (`errors.rateLimited`); the old spelling here matched nothing,
  // so every 429 fell through to the generic line.
  'request.rate_limited': 'error.rateLimited',
  'avatar.unsupported_type': 'error.unsupportedType',
  'avatar.too_large': 'error.tooLarge',
  'storage.upload_rejected': 'error.unsupportedType',
  'social.too_long': 'error.social.tooLong',
  'social.needs_media': 'error.social.needsMedia',
  'social.missing_token': 'error.social.missingToken',
  'social.not_waiting': 'error.social.notWaiting',
  'social.already_out': 'error.social.alreadyOut',
  'social.cannot_cancel': 'error.social.cannotCancel',
  'ads.missing_token': 'error.ads.missingToken',
  'ads.missing_account_id': 'error.ads.missingAccountId',
  'ads.missing_developer_token': 'error.ads.missingDeveloperToken',
  'ads.account_taken': 'error.ads.accountTaken',
  'integrations.scope_not_held': 'error.integrations.scopeNotHeld',
  'integrations.bad_pattern': 'error.integrations.badPattern',
  'integrations.bad_url': 'error.integrations.badUrl',
  'chat.last_admin': 'error.chat.lastAdmin',
  'sms.topup_amount': 'error.sms.topupAmount',
  'chat.remove_self': 'error.chat.removeSelf',
  'ai.no_provider': 'error.ai.noProvider',
  'inbox.nothing_to_summarise': 'error.inbox.nothingToSummarise',
  'inbox.summary_empty': 'error.inbox.summaryEmpty',
  'branding.bad_color': 'error.branding.badColor',
  'branding.css_import': 'error.branding.cssRemote',
  'branding.css_remote_url': 'error.branding.cssRemote',
  'branding.css_script': 'error.branding.cssScript',
  'branding.css_closes_style': 'error.branding.cssScript',
  'branding.css_too_large': 'error.branding.cssTooLarge',
  'branding.unsupported_type': 'error.unsupportedType',
  'branding.too_large': 'error.tooLarge',
};

export function messageFor(error: unknown): string {
  if (!(error instanceof ApiRequestError)) return t('error.generic');

  const key = MESSAGES[error.code];

  if (key) return t(key);

  // A code with no translation is still more useful than a generic line when it names the resource.
  return t('error.generic');
}

export function requestIdOf(error: unknown): string | null {
  return error instanceof ApiRequestError ? error.requestId : null;
}

/** The API's code on a failure, or null for anything that is not one of its answers. */
export function codeOf(error: unknown): string | null {
  return error instanceof ApiRequestError ? error.code : null;
}
